import { useId } from 'react'
import { format } from 'date-fns'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { EquityCurvePoint } from '../../types'
import { formatPnlPct } from '../../utils/pnl'
import { parseApiDate } from '../../utils/signalExpiry'
import { ChartTheme, directionOf, niceScale, useChartTheme } from './chartTheme'
import { displaySymbol } from '../../utils/displaySymbol'

// Past this many closes the per-trade outcome dots crowd the line.
const MAX_OUTCOME_DOTS = 60

export interface CurvePoint {
  /** Trade number in close order; 0 is the flat starting point. */
  n: number
  cumulative: number
  /** This close's contribution to `cumulative`: its gross move, before fees. */
  pnl: number | null
  symbol: string | null
  side?: 'long' | 'short'
  win: boolean | null
  time: number | null
  /** Daily account curves: the account value behind `cumulative`. */
  equity?: number
  openPositions?: number
}

/** 'closes': one point per closed trade, x is the trade number. 'days': one per daily snapshot, x is days since launch. */
export type CurveMode = 'closes' | 'days'

const money = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Win, loss or (null) breakeven. Older API builds sent no outcome; the sign of the P&L is the same call. */
export const curveOutcome = (point: EquityCurvePoint): boolean | null =>
  point.win !== undefined
    ? point.win
    : point.profit_loss_pct > 0
    ? true
    : point.profit_loss_pct < 0
    ? false
    : null

/**
 * The chart's points, led by a flat start at zero. Gross, before fees, like
 * every result a user sees: the per-close move and the API's own running sum.
 * A fee is applied only in the fee calculator, which draws its own curve.
 */
export const toCurvePoints = (curve: EquityCurvePoint[]): CurvePoint[] => {
  if (!curve.length) return []
  return [
    { n: 0, cumulative: 0, pnl: null, symbol: null, win: null, time: null },
    ...curve.map((point, index) => {
      return {
        n: index + 1,
        cumulative: point.cumulative_pnl_pct,
        pnl: point.profit_loss_pct,
        symbol: point.symbol,
        side: point.signal_type,
        win: curveOutcome(point),
        time: point.timestamp ? parseApiDate(point.timestamp).getTime() : null,
      }
    }),
  ]
}

/** Deepest fall from a running peak (the start counts as one), in percentage points; 0 if it never fell. */
export const maxDrawdown = (points: CurvePoint[]): number => {
  let peak = 0
  let deepest = 0
  for (const point of points) {
    peak = Math.max(peak, point.cumulative)
    deepest = Math.max(deepest, peak - point.cumulative)
  }
  return deepest
}

const outcomeColor = (win: boolean | null, colors: ChartTheme['colors']) =>
  win === true ? colors.up : win === false ? colors.down : colors.flat

const outcomeWord = (win: boolean | null) => (win === true ? 'win' : win === false ? 'loss' : 'breakeven')

interface CurveTooltipProps {
  active?: boolean
  payload?: Array<{ payload: CurvePoint }>
}

const CurveTooltip = ({ active, payload }: CurveTooltipProps) => {
  const chart = useChartTheme()
  if (!active || !payload?.length) return null
  const point = payload[0].payload
  if (point.equity != null) {
    return (
      <div className="rounded-md border border-terminal-border bg-terminal-raised px-3 py-2 shadow-lg">
        <p className="text-xs text-gray-400">
          {point.n === 0 ? 'Launch' : `Day ${point.n}`}
          {point.time != null ? ` · ${format(point.time, 'MMM d, yyyy')}` : ''}
        </p>
        <p
          className="font-mono text-sm font-semibold tabular-nums"
          style={{ color: chart.colors[directionOf(point.cumulative)] }}
        >
          {formatPnlPct(point.cumulative)}
        </p>
        <p className="font-mono text-xs tabular-nums text-gray-300">{money(point.equity)}</p>
        {point.openPositions != null && (
          <p className="text-xs text-gray-500">
            {point.openPositions} open position{point.openPositions === 1 ? '' : 's'}
          </p>
        )}
      </div>
    )
  }
  return (
    <div className="rounded-md border border-terminal-border bg-terminal-raised px-3 py-2 shadow-lg">
      {point.n === 0 ? (
        <p className="text-xs text-gray-400">Start · 0.00%</p>
      ) : (
        <>
          <p className="text-xs text-gray-400">
            #{point.n} · {displaySymbol(point.symbol ?? '')}
            {point.side ? ` ${point.side}` : ''}
          </p>
          <p className="font-mono text-sm font-semibold tabular-nums" style={{ color: outcomeColor(point.win, chart.colors) }}>
            {formatPnlPct(point.pnl ?? 0)} · {outcomeWord(point.win)}
          </p>
          <p className="font-mono text-xs tabular-nums text-gray-300">
            cumulative {formatPnlPct(point.cumulative)} before fees
          </p>
          {point.time != null && <p className="text-xs text-gray-500">{format(point.time, 'MMM d, HH:mm')}</p>}
        </>
      )}
    </div>
  )
}

/**
 * Cumulative P&L over closed signals: every close plotted as it happened, no
 * smoothing, so drawdowns show at full depth. Green above break-even and red
 * below (line and fill split exactly at zero), a dashed break-even line, each
 * close dotted by its outcome, and a hover card per point. In 'days' mode it
 * plots a daily account-equity series instead, without outcome dots. Fills its
 * parent's height.
 */
const EquityCurveChart = ({
  points,
  ariaLabel,
  mode = 'closes',
}: {
  points: CurvePoint[]
  ariaLabel: string
  mode?: CurveMode
}) => {
  const chartId = useId().replace(/:/g, '')
  const chart = useChartTheme()
  const strokeId = `equity-stroke-${chartId}`
  const fillId = `equity-fill-${chartId}`

  const values = points.map((point) => point.cumulative)
  const high = Math.max(0, ...values)
  const low = Math.min(0, ...values)
  // The line and its fill both span [low, high] (the fill runs to zero), so
  // one offset splits both gradients exactly at the zero line.
  const zeroOffset = high === low ? 0.5 : high / (high - low)
  const flatLine = high === low
  const span = high - low || 1
  const yScale = niceScale([low - span * 0.06, high + span * 0.06])
  const net = values.length ? values[values.length - 1] : null
  // A daily snapshot is not an outcome; only per-close curves get outcome dots.
  const showDots = mode === 'closes' && points.length - 1 <= MAX_OUTCOME_DOTS

  return (
    <div role="img" aria-label={ariaLabel} data-testid="equity-curve" data-direction={directionOf(net)} className="h-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={strokeId} x1="0" y1="0" x2="0" y2="1">
              <stop offset={zeroOffset} stopColor={chart.colors.up} />
              <stop offset={zeroOffset} stopColor={chart.colors.down} />
            </linearGradient>
            <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset={0} stopColor={chart.colors.up} stopOpacity={0.3} />
              <stop offset={zeroOffset} stopColor={chart.colors.up} stopOpacity={0.02} />
              <stop offset={zeroOffset} stopColor={chart.colors.down} stopOpacity={0.02} />
              <stop offset={1} stopColor={chart.colors.down} stopOpacity={0.3} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="n"
            type="number"
            domain={[0, 'dataMax']}
            allowDecimals={false}
            tickFormatter={(n: number) => (n === 0 ? 'start' : mode === 'days' ? `day ${n}` : `#${n}`)}
            tick={chart.axisTick}
            tickLine={false}
            axisLine={{ stroke: chart.grid }}
            minTickGap={24}
          />
          <YAxis
            orientation="right"
            domain={yScale.domain}
            ticks={yScale.ticks}
            tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${Number(v.toFixed(2))}%`}
            tick={chart.axisTick}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <ReferenceLine y={0} stroke={chart.cursor} strokeDasharray="4 4" />
          <ChartTooltip
            content={<CurveTooltip />}
            cursor={{ stroke: chart.cursor, strokeWidth: 1, strokeDasharray: '3 3' }}
            isAnimationActive={false}
          />
          {/* Linear, not monotone: a curve between closes would invent highs and lows that never happened. */}
          <Area
            type="linear"
            dataKey="cumulative"
            baseValue={0}
            stroke={flatLine ? chart.colors.flat : `url(#${strokeId})`}
            strokeWidth={2}
            fill={flatLine ? 'none' : `url(#${fillId})`}
            isAnimationActive={false}
            dot={
              showDots
                ? (props: { cx?: number; cy?: number; payload?: CurvePoint }) =>
                    props.payload && props.payload.n > 0 && props.cx != null && props.cy != null ? (
                      <circle
                        key={props.payload.n}
                        cx={props.cx}
                        cy={props.cy}
                        r={3}
                        fill={outcomeColor(props.payload.win, chart.colors)}
                        stroke={chart.surface}
                        strokeWidth={1.5}
                      />
                    ) : (
                      <g key="start" />
                    )
                : false
            }
            activeDot={(props: { cx?: number; cy?: number; payload?: CurvePoint }) => (
              <circle
                cx={props.cx}
                cy={props.cy}
                r={5}
                fill={props.payload ? outcomeColor(props.payload.win, chart.colors) : chart.colors.flat}
                stroke={chart.surface}
                strokeWidth={2}
              />
            )}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default EquityCurveChart
